import React, { useState, useEffect } from 'react';
import { BasicFacet, BasicFacetValue } from '../types';

interface AkcijaFacetComponentProps {
  facet: BasicFacet;
  facetKey: string;
  isExpanded: boolean;
  selectedFilters: Record<string, Set<string>>;
  onFilterChange: (facetKey: string, value: BasicFacetValue, checked: boolean, type: string | null) => void;
  sanitizeFilterValue: (value: string) => string;
  isFilterSelected: (facet: BasicFacet, value: BasicFacetValue) => boolean;
}

export const AkcijaFacetComponent: React.FC<AkcijaFacetComponentProps> = ({
  facet,
  facetKey,
  isExpanded,
  onFilterChange,
  sanitizeFilterValue,
  isFilterSelected
}) => {
  const [akcijaMap, setAkcijaMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch akcija labels from the API
  useEffect(() => {
    const fetchAkcijaLabels = async () => {
      try {
        setLoading(true);
        const response = await fetch('/group_page/akcijaLabels');
        
        if (!response.ok) {
          throw new Error(`Failed to fetch akcija labels: ${response.status}`);
        }
        
        const data = await response.json();
        setAkcijaMap(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching akcija labels:', err);
        setError('Failed to load akcija labels');
        // Fallback to empty map if API call fails
        setAkcijaMap({});
      } finally {
        setLoading(false);
      }
    };

    fetchAkcijaLabels();
  }, []);

  // Filter out values that are not in the akcijaMap
  const filteredValues = facet.values.filter(value => 
    akcijaMap[value.filter] !== undefined
  );

  if (!isExpanded) {
    return null;
  }

  if (loading) {
    return <div className="ml-2 p-2 text-sm text-gray-500">Loading akcija labels...</div>;
  }

  if (error) {
    return <div className="ml-2 p-2 text-sm text-red-500">{error}</div>;
  }

  return (
    <div className="ml-2 space-y-1">
      {filteredValues.map((item) => {
        const sanitizedFilter = sanitizeFilterValue(item.filter);
        const label = akcijaMap[item.filter] || sanitizedFilter;
        
        return (
          <div key={item.filter} className="flex items-top">
            <input
              type="checkbox"
              id={`${facetKey}-${sanitizedFilter}`}
              checked={isFilterSelected(facet, item)}
              onChange={(e) => onFilterChange(facetKey, item, e.target.checked, facet.type)}
              className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label 
              htmlFor={`${facetKey}-${sanitizedFilter}`}
              className="text-sm text-gray-700 cursor-pointer flex items-top justify-between w-full"
            >
              <span>{label}</span>
              <span className="text-xs text-gray-500 ml-1">({item.count})</span>
            </label>
          </div>
        );
      })}
    </div>
  );
}; 